import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Card, Empty, Field } from "../../components/sc/ui";
import { TablePager } from "../../components/sc/Pager";

const INK = "#14101F";
const VIOLET = "#4931E8";
const LIME = "#C7F23D";

const EVENTS = [
  { key: "payment.success", label: "payment.success" },
  { key: "payment.expired", label: "payment.expired" },
  { key: "payment.failed", label: "payment.failed" },
  { key: "settlement.completed", label: "settlement.completed" },
  { key: "refund.processed", label: "refund.processed" },
];

export default function Callbacks() {
  const [endpoint, setEndpoint] = useState(null);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState({});
  const [showSecret, setShowSecret] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [dPage, setDPage] = useState(1);
  const [dSize, setDSize] = useState(10);
  const [dTotal, setDTotal] = useState(0);
  // The 5s poll is created once, so it reads the current page through a ref.
  const dRef = useRef({ page: 1, size: 10 });
  dRef.current = { page: dPage, size: dSize };

  // Browser redirect callbacks (success/failure/cancel/back URLs)
  const [successUrl, setSuccessUrl] = useState("");
  const [failureUrl, setFailureUrl] = useState("");
  const [cancelUrl, setCancelUrl] = useState("");
  const [backUrl, setBackUrl] = useState("");
  const [cbSecret, setCbSecret] = useState("");
  const [cbBusy, setCbBusy] = useState(false);
  const [showCbSecret, setShowCbSecret] = useState(false);

  // Poll ONLY the read-only deliveries list. The interval must never touch the
  // editable inputs (url / success / failure / cancel / back) or it clobbers what
  // the user is typing every few seconds.
  const loadDeliveries = async () => {
    try {
      const { page, size } = dRef.current;
      const d = await api.get("/webhooks/deliveries", { params: { page, page_size: size } });
      setDeliveries(d.data.items || []);
      setDTotal(d.data.total || 0);
      if (d.data.page && d.data.page !== page) setDPage(d.data.page);
    } catch { /* ignore */ }
  };

  // One-time full load: populate the editable fields from the server. Called on
  // mount only — NOT on the interval.
  const load = async () => {
    try {
      const { data } = await api.get("/webhooks/endpoint");
      setEndpoint(data);
      setUrl(data.url || "");
      setEvents(data.events || {});
    } catch { /* ignore */ }
    try {
      const cb = await api.get("/merchants/me/callbacks");
      setSuccessUrl(cb.data.success_url || "");
      setFailureUrl(cb.data.failure_url || "");
      setCancelUrl(cb.data.cancel_url || "");
      setBackUrl(cb.data.back_url || "");
      setCbSecret(cb.data.callback_secret || "");
    } catch { /* ignore */ }
  };

  const saveCallbacks = async () => {
    setCbBusy(true);
    try {
      const { data } = await api.patch("/merchants/me/callbacks", {
        success_url: successUrl.trim(),
        failure_url: failureUrl.trim(),
        cancel_url: cancelUrl.trim(),
        back_url: backUrl.trim(),
      });
      setSuccessUrl(data.success_url || "");
      setFailureUrl(data.failure_url || "");
      setCancelUrl(data.cancel_url || "");
      setBackUrl(data.back_url || "");
      setCbSecret(data.callback_secret || "");
      toast.success("Redirect URLs saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setCbBusy(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(loadDeliveries, 5000); // poll deliveries only — never the inputs
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDeliveries(); }, [dPage, dSize]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/webhooks/endpoint", { url, events });
      setEndpoint(data);
      setSavedMsg("✓ Endpoint saved");
      setTimeout(() => setSavedMsg(""), 2500);
      toast.success("Webhook endpoint saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post("/webhooks/test");
      toast.success("Test event queued — check Recent deliveries");
      setTimeout(loadDeliveries, 1500); // refresh deliveries only — don't wipe inputs
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const toggleEvent = async (k) => {
    const next = { ...events, [k]: !events[k] };
    setEvents(next);
    if (endpoint?.id) {
      try {
        await api.patch("/webhooks/endpoint/events", { events: next });
      } catch {
        /* ignore */
      }
    }
  };

  const secretValue = endpoint?.signing_secret || "—";
  const secretRendered = showSecret ? secretValue : "•".repeat(Math.max(12, (secretValue || "").length - 4)) + (secretValue?.slice(-4) || "");


  const code = (t) => <span className="ui-mono">{t}</span>;

  return (
    <div data-testid="callbacks-page" className="ui-page cb-page">
      <div className="cb-grid">
        <div className="cb-col">
          <Card data-testid="redirect-callbacks-card" title="Redirect callbacks"
            sub="Where we send the customer’s browser after a payment. The redirect is HMAC-signed — verify it (or wait for the webhook) before fulfilling.">
            <div className="cb-fields">
              <Field label="Success URL">
                <input
                  data-testid="callback-success-url"
                  value={successUrl}
                  onChange={(e) => setSuccessUrl(e.target.value)}
                  placeholder="https://your-store.com/order/success"
                  className="ui-input is-mono"
                />
              </Field>
              <Field label="Failure / timeout URL">
                <input
                  data-testid="callback-failure-url"
                  value={failureUrl}
                  onChange={(e) => setFailureUrl(e.target.value)}
                  placeholder="https://your-store.com/order/failed"
                  className="ui-input is-mono"
                />
              </Field>
              <Field label={<>Cancel URL <span className="cb-opt">· optional</span></>}
                hint={<>Where “Cancel” on the pay page sends the customer (we append {code("?sc_status=cancelled")}).</>}>
                <input
                  data-testid="callback-cancel-url"
                  value={cancelUrl}
                  onChange={(e) => setCancelUrl(e.target.value)}
                  placeholder="https://your-store.com/cart"
                  className="ui-input is-mono"
                />
              </Field>
              <Field label={<>Back-to-merchant URL <span className="cb-opt">· optional</span></>}
                hint={<>Shows a “Back to {`{store}`}” button on the pay page (e.g. your storefront/home).</>}>
                <input
                  data-testid="callback-back-url"
                  value={backUrl}
                  onChange={(e) => setBackUrl(e.target.value)}
                  placeholder="https://your-store.com"
                  className="ui-input is-mono"
                />
              </Field>
            </div>
            <button data-testid="callback-save" type="button" onClick={saveCallbacks} disabled={cbBusy} className="ui-btn ui-btn-primary cb-cta">
              {cbBusy ? "Saving…" : "Save redirect URLs"}
            </button>

            <div className="cb-secret-block">
              <div className="ui-between" style={{ marginBottom: 6 }}>
                <span className="ui-eyebrow">Callback signing secret</span>
                <button data-testid="callback-secret-toggle" type="button" onClick={() => setShowCbSecret((v) => !v)} className="ui-btn ui-btn-ghost ui-btn-sm cb-reveal">{showCbSecret ? "hide" : "reveal"}</button>
              </div>
              <div className="sc-scroll cb-secret" data-testid="callback-secret">
                {showCbSecret ? (cbSecret || "—") : "•".repeat(Math.max(12, (cbSecret || "").length - 4)) + (cbSecret?.slice(-4) || "")}
              </div>
              <p className="ui-faint cb-note">
                Verify {code("sc_sig")} = HMAC-SHA256 of the sorted {code("sc_*")} query params (excluding {code("sc_sig")}) with this secret.
              </p>
            </div>
          </Card>

          <Card data-testid="webhook-endpoint-card" title="Webhook endpoint" sub="We POST a signed event here the instant a payment lands.">
            <Field label="Endpoint URL">
              <input
                data-testid="webhook-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://your-app.com/webhooks/supercash"
                className="ui-input is-mono"
              />
            </Field>
            <div className="ui-row cb-actions">
              <button data-testid="webhook-save" type="button" onClick={save} disabled={busy} className="ui-btn ui-btn-primary cb-cta">
                {busy ? "Saving…" : "Save endpoint"}
              </button>
              <button data-testid="webhook-test" type="button" onClick={sendTest} disabled={!endpoint?.url} className="ui-btn cb-cta">Send test event</button>
            </div>
            {savedMsg && <div className="cb-saved ui-mono">{savedMsg}</div>}
          </Card>

          <PayoutWebhookCard />

          <Card data-testid="webhook-events-card" title="Subscribed events" sub="Tap to toggle which events we deliver.">
            <div className="cb-events">
              {EVENTS.map((e) => (
                <button key={e.key} type="button" data-testid={`event-${e.key}`} onClick={() => toggleEvent(e.key)}
                  className={`cb-event ${events[e.key] ? "is-on" : ""}`} role="switch" aria-checked={!!events[e.key]}>
                  <span className="ui-mono cb-event-label">{e.label}</span>
                  <span className={`ui-switch ${events[e.key] ? "is-on" : ""}`} aria-hidden="true" />
                </button>
              ))}
            </div>
          </Card>
        </div>

        <div className="cb-col">
          <section className="ui-card cb-dark" data-testid="signing-secret-card">
            <div className="ui-between" style={{ marginBottom: 10 }}>
              <span className="ui-eyebrow cb-lime">Signing secret</span>
              <button data-testid="signing-toggle" type="button" onClick={() => setShowSecret((v) => !v)} className="cb-dark-btn">{showSecret ? "hide" : "reveal"}</button>
            </div>
            <div className="sc-scroll cb-dark-well" data-testid="signing-value">
              {secretRendered}
            </div>
            <p className="cb-dark-note">
              Verify the <span className="ui-mono cb-lime">X-SuperCash-Signature</span> header (HMAC-SHA256) against the raw body using this secret.
            </p>
          </section>

          <section className="ui-card cb-dark" data-testid="sample-payload-card">
            <span className="ui-eyebrow cb-lime">Sample payload</span>
            <pre className="sc-scroll cb-pre">{`{
  "event": "payment.success",
  "data": {
    "id": "pay_8Kp2Xa",
    "amount": 500.37,
    "payer_vpa": "payer@bank",
    "vpa": "yourvpa@bank",
    "rrn": "428931746520",
    "customer_name": "Rohan Mehta",
    "customer_phone": "+91 98765 43210",
    "confirmation_source": "auto"
  }
}`}</pre>
          </section>

          <Card data-testid="recent-deliveries" title="Recent deliveries" pad={false}>
            {deliveries.length === 0 && (
              <Empty icon="📭" title="No deliveries yet">Hit "Send test event" above.</Empty>
            )}
            {deliveries.length > 0 && (
              <div className="cb-deliveries">
                {deliveries.map((d) => (
                  <div key={d.id} className="cb-delivery" data-testid={`delivery-${d.id}`}>
                    <span className="ui-mono ui-trunc cb-delivery-event" title={d.event}>{d.event}</span>
                    <span className="ui-row" style={{ gap: 10, flexWrap: "nowrap", flex: "none" }}>
                      {d.status === "retrying" && <span className="ui-mono" style={{ fontSize: 11, color: "#b26a00" }} title={d.next_attempt_at ? `Next retry ${new Date(d.next_attempt_at).toLocaleTimeString("en-IN")}` : ""}>retrying · {d.attempts} tries</span>}
                      {d.status === "failed" && <span className="ui-mono" style={{ fontSize: 11, color: "#b3261e" }}>gave up · {d.attempts} tries</span>}
                      {d.can_resend && d.status !== "delivered" && (
                        <button type="button" className="ui-btn ui-btn-sm" style={{ minHeight: 28, padding: "2px 10px" }} data-testid={`delivery-resend-${d.id}`}
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/webhooks/deliveries/${d.id}/resend`);
                              if (data.status === "delivered") toast.success(`Delivered (HTTP ${data.status_code})`);
                              else toast.error(`Still failing: ${data.error || `HTTP ${data.status_code}`}`);
                              loadDeliveries();
                            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Resend failed"); }
                          }}>Resend</button>
                      )}
                      <span className="ui-mono ui-muted" style={{ fontSize: 11 }}>{d.latency_ms}ms</span>
                      <span className={`sc-status sc-status-${d.status_code >= 200 && d.status_code < 300 ? "settled" : "pending"}`} style={{ minWidth: 38, textAlign: "center" }}>{d.status_code || "—"}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
            <TablePager testId="deliveries-pager" page={dPage} total={dTotal} size={dSize} onPage={setDPage} onSize={setDSize} />
          </Card>
        </div>
      </div>

      <style>{`
        .cb-page .cb-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 18px; align-items: start; }
        .cb-page .cb-col { display: flex; flex-direction: column; gap: 18px; min-width: 0; }
        .cb-page .cb-fields { display: flex; flex-direction: column; gap: 14px; margin-bottom: 16px; }
        .cb-page .cb-fields .ui-field > span { text-transform: none; font-size: 11.5px; letter-spacing: 0; }
        .cb-page .cb-fields .ui-field small { line-height: 1.45; overflow-wrap: anywhere; }
        .cb-page .cb-opt { color: var(--ui-faint); font-weight: 400; }
        .cb-page .cb-cta { min-height: 42px; padding: 9px 20px; font-size: 14px; }
        .cb-page .cb-actions { margin-top: 14px; }
        .cb-page .cb-secret-block { margin-top: 18px; padding-top: 16px; border-top: 2px dashed var(--ui-line); }
        .cb-page .cb-reveal { color: ${VIOLET}; font-family: "JetBrains Mono", monospace; min-height: 32px; }
        .cb-page .cb-secret { background: #FBFAF7; border: 2px solid ${INK}; border-radius: 10px; padding: 10px 12px; font-family: "JetBrains Mono", monospace; font-size: 12px; overflow-x: auto; white-space: nowrap; }
        .cb-page .cb-note { font-size: 11.5px; margin: 8px 0 0; line-height: 1.5; }
        .cb-page .cb-saved { margin-top: 14px; background: #DFF7E7; border: 2px solid #137a47; border-radius: 10px; padding: 10px 13px; font-size: 12px; font-weight: 700; color: #137a47; }

        .cb-page .cb-events { display: flex; flex-direction: column; gap: 10px; }
        .cb-page .cb-event { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; min-height: 48px; padding: 10px 14px; background: #FBFAF7; border: 2px solid ${INK}; border-radius: 12px; cursor: pointer; color: ${INK}; text-align: left; transition: background .12s, box-shadow .12s, transform .08s; }
        .cb-page .cb-event:hover { background: #fff; box-shadow: 3px 3px 0 ${INK}; transform: translate(-1px, -1px); }
        .cb-page .cb-event:focus-visible { outline: 3px solid ${VIOLET}; outline-offset: 2px; }
        .cb-page .cb-event.is-on { background: #ECE8FB; }
        .cb-page .cb-event .ui-switch { pointer-events: none; }
        .cb-page .cb-event .ui-switch.is-on { background: ${VIOLET}; }
        .cb-page .cb-event-label { font-size: 13px; font-weight: 700; min-width: 0; overflow-wrap: anywhere; }

        .cb-page .cb-dark { background: ${INK}; color: #fff; padding: 22px; }
        .cb-page .cb-lime { color: ${LIME}; }
        .cb-page .cb-dark-btn { cursor: pointer; background: #221d36; color: #fff; border: 2px solid #3a3550; border-radius: 9px; font-family: "JetBrains Mono", monospace; font-size: 11px; font-weight: 700; padding: 6px 12px; min-height: 34px; }
        .cb-page .cb-dark-btn:hover { border-color: ${LIME}; color: ${LIME}; }
        .cb-page .cb-dark-well { background: #221d36; border: 2px solid #3a3550; border-radius: 10px; padding: 11px 13px; font-family: "JetBrains Mono", monospace; font-size: 12.5px; overflow-x: auto; white-space: nowrap; }
        .cb-page .cb-dark-note { font-size: 12px; line-height: 1.55; color: #b6b0c8; margin: 12px 0 0; overflow-wrap: anywhere; }
        .cb-page .cb-pre { margin: 10px 0 0; overflow-x: auto; font-family: "JetBrains Mono", monospace; font-size: 12px; line-height: 1.65; white-space: pre; color: #e9e6f2; }

        .cb-page .cb-deliveries { display: flex; flex-direction: column; }
        .cb-page .cb-delivery { display: flex; justify-content: space-between; align-items: center; gap: 12px; font-size: 12.5px; padding: 11px 20px; border-bottom: 1px solid var(--ui-line-soft); min-width: 0; }
        .cb-page .cb-delivery:last-child { border-bottom: none; }
        .cb-page .cb-delivery-event { font-weight: 700; min-width: 0; }

        @media (max-width: 980px) {
          .cb-page .cb-grid { grid-template-columns: minmax(0, 1fr); }
        }
        @media (max-width: 480px) {
          .cb-page .cb-grid, .cb-page .cb-col { gap: 14px; }
          .cb-page .cb-dark { padding: 16px; }
          .cb-page .cb-delivery { padding: 10px 15px; }
          .cb-page .cb-actions .ui-btn, .cb-page .cb-cta { flex: 1 1 auto; width: 100%; }
          .cb-page .cb-actions { flex-direction: column; align-items: stretch; }
          .cb-page .cb-pre { font-size: 11px; }
          .cb-page .cb-event-label { font-size: 12px; }
        }
      `}</style>
    </div>
  );
}

// Payout callbacks (payout.success / payout.failed) can go to their own URL; when
// empty they fall back to the main webhook endpoint above. Same signing secret.
function PayoutWebhookCard() {
  const [url, setUrl] = useState("");
  const [fallback, setFallback] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/gateway/payout-webhook").then(({ data }) => { setUrl(data.url || ""); setFallback(data.fallback_url || ""); }).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/gateway/payout-webhook", { url: url.trim() });
      setUrl(data.url || "");
      toast.success(data.url ? "Payout webhook saved" : "Payout webhook cleared — using the main endpoint");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card data-testid="payout-webhook-card" title="Payout webhook" sub="Where we POST payout.success / payout.failed. Signed with the same secret as all webhooks.">
      <Field label="Payout endpoint URL" hint={fallback ? `Leave empty to use the main endpoint (${fallback}).` : "Leave empty to use the main endpoint above."}>
        <input data-testid="payout-webhook-url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://your-app.com/webhooks/payouts" className="ui-input is-mono" />
      </Field>
      <div className="ui-row cb-actions">
        <button data-testid="payout-webhook-save" type="button" onClick={save} disabled={busy} className="ui-btn ui-btn-primary cb-cta">
          {busy ? "Saving…" : "Save payout endpoint"}
        </button>
      </div>
    </Card>
  );
}
